import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Dialog,
    DialogContent,
    IconButton,
    Grid,
    Chip,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Avatar,
    alpha
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CategoryIcon from "@mui/icons-material/CategoryOutlined";
import LabelIcon from "@mui/icons-material/LabelOutlined";
import BrandingWatermarkIcon from "@mui/icons-material/BrandingWatermarkOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import LayersIcon from "@mui/icons-material/LayersOutlined";
import { getProductById } from "../../service/productService";
import Color from "../../components/Color";

const NO_IMAGE =
    "https://media.istockphoto.com/id/1128826884/vector/no-image-vector-symbol-missing-available-icon-no-gallery-for-this-moment.jpg?s=612x612&w=0&k=20&c=390e76zN_TJ7HZHJpnI7jNl7UBpO3UP7hpR2meE1Qd4=";

const ViewProduct = ({ open, onClose, productId }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && productId) {
            const fetchProduct = async () => {
                setLoading(true);
                try {
                    const res = await getProductById(productId);
                    setProduct(res.data.data);
                } catch (error) {
                    console.error("Failed to fetch product details", error);
                } finally {
                    setLoading(false);
                }
            };
            fetchProduct();
        } else {
            setProduct(null);
        }
    }, [open, productId]);

    const imageUrl =
        product?.image_url && product.image_url !== "http://127.0.0.1:8000/"
            ? product.image_url
            : product?.category?.image_url || NO_IMAGE;

    const gstRate = product?.gst_percentage != null
        ? parseFloat(product.gst_percentage)
        : (product?.tax_rate != null ? parseFloat(product.tax_rate) : 0);

    const isInclusive = product?.price_includes_tax !== false;

    const inclusivePrice = isInclusive
        ? parseFloat(product?.inclusive_price ?? product?.base_price ?? 0)
        : parseFloat(product?.base_price || 0) * (1 + gstRate / 100);

    const basePrice = product?.base_price != null
        ? parseFloat(product.base_price)
        : (inclusivePrice / (1 + gstRate / 100));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
                    background: "#ffffff",
                    border: "1px solid #e0e0e0",
                },
            }}
            TransitionProps={{ timeout: 400 }}
        >
            <DialogContent sx={{ p: 0, position: "relative", bgcolor: "#fff", minHeight: 450 }}>
                {loading ? (
                    <Box sx={{
                        height: 500,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        // background: `radial-gradient(circle at center, #fff 0%, #f9f9fc 100%)`
                    }}>
                        <CircularProgress size={50} thickness={4} sx={{ color: Color.primary.main, opacity: 0.8 }} />
                        <Typography variant="overline" sx={{ color: "text.disabled", fontWeight: 700, letterSpacing: 2 }}>
                            Retrieving Dossier...
                        </Typography>
                    </Box>
                ) : product ? (
                    <>
                        {/* ── IMMERSIVE HERO SECTION ── */}
                        <Box sx={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            minHeight: isMobile ? "auto" : 450,
                            // borderBottom: "1px solid #f0f0f5"
                        }}>
                            {/* Left: Product Image Area */}
                            <Box sx={{
                                flex: { xs: "none", md: 1.2 },
                                // bgcolor: "#f9f9fc",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 4,
                                position: "relative",
                                overflow: "hidden"
                            }}>
                                {/* Decorative background element */}
                                <Box sx={{
                                    position: "absolute",
                                    width: "150%",
                                    height: "150%",
                                    // background: `radial-gradient(circle, ${alpha(Color.primary.main, 0.05)} 0%, transparent 70%)`,   
                                    top: "-25%",
                                    left: "-25%"
                                }} />

                                <img
                                    src={imageUrl}
                                    alt={product?.name}
                                    style={{
                                        width: "100%",
                                        height: "auto",
                                        maxHeight: 300,
                                        objectFit: "contain",
                                        // filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.1))",
                                        zIndex: 1
                                    }}
                                    onError={(e) => { e.target.src = NO_IMAGE; }}
                                />

                                <IconButton
                                    onClick={onClose}
                                    size="small"
                                    sx={{
                                        position: "absolute",
                                        top: 20,
                                        left: 20,
                                        bgcolor: "white",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                                        color: "text.secondary",
                                        "&:hover": { bgcolor: "#f8f9fa", transform: "scale(1.1)" },
                                        zIndex: 10
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            {/* Right: Primary Info Dossier */}
                            <Box sx={{
                                flex: 1,
                                p: { xs: 3, md: 5 },
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center"
                            }}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="overline" sx={{ color: Color.primary.main, fontWeight: 900, letterSpacing: 2 }}>
                                        Product Profile
                                    </Typography>
                                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800} sx={{ color: "text.primary", mt: 1, lineHeight: 1.1 }}>
                                        {loading ? "Syncing..." : product?.name}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                                        <Chip
                                            label={product?.is_active ? "● ACTIVE" : "○ INACTIVE"}
                                            sx={{
                                                bgcolor: "transparent",
                                                color: product?.is_active ? "#2e7d32" : "#d32f2f",
                                                fontWeight: 800,
                                                fontSize: "0.75rem",
                                                border: "1px solid currentColor"
                                            }}
                                        />
                                        <Chip
                                            label={product?.is_favourite ? "♥ FAVOURITE" : "♡ REGULAR"}
                                            sx={{
                                                bgcolor: "transparent",
                                                color: product?.is_favourite ? "#e91e63" : "text.disabled",
                                                fontWeight: 800,
                                                fontSize: "0.75rem",
                                                border: "1px solid currentColor"
                                            }}
                                        />
                                        {/* <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 700 }}>
                                            REF #PN-{product?.id}
                                        </Typography> */}
                                    </Box>
                                </Box>

                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <Box sx={{ display: "flex", gap: { xs: 3, sm: 5 }, flexWrap: "wrap", alignItems: "flex-end" }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                Price (Incl. Tax)
                                            </Typography>
                                            <Typography variant="h3" fontWeight={900} color={Color.primary.main}>
                                                ₹{inclusivePrice.toFixed(2)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ pb: { xs: 0, sm: 0.5 } }}>
                                            <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                Base Price (Excl. Tax)
                                            </Typography>
                                            <Typography variant="h4" fontWeight={800} color="text.secondary">
                                                ₹{basePrice.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: "flex", gap: 4, pt: 2, borderTop: "1px solid #f0f0f5" }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 700, display: "block" }}>SHORT CODE</Typography>
                                            <Typography variant="h6" fontWeight={800} color="text.primary">
                                                {product?.short_code || "---"}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ borderLeft: "1px solid #f0f0f5", pl: 4 }}>
                                            <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 700, display: "block" }}>HSN CODE</Typography>
                                            <Typography variant="h6" fontWeight={800} color="text.primary">
                                                {product?.hsn_code || "---"}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ borderLeft: "1px solid #f0f0f5", pl: 4 }}>
                                            <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 700, display: "block" }}>UNIT SCALE</Typography>
                                            <Typography variant="h6" fontWeight={800} color="text.primary">
                                                {product?.unit_quantity ? `${product.unit_quantity} ${product.units || ""}` : "System Unit"}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ borderLeft: "1px solid #f0f0f5", pl: 4 }}>
                                            <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 700, display: "block" }}>GST %</Typography>
                                            <Typography variant="h6" fontWeight={800} color="text.primary">
                                                {product?.gst_percentage != null ? `${parseFloat(product.gst_percentage)}%` : (product?.tax_rate != null ? `${parseFloat(product.tax_rate)}%` : "0%")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        {/* ── SECONDARY CLASSIFICATION LIST ── */}
                        <Box sx={{ p: { xs: 3, md: 5 } }}>
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={product?.description ? 6 : 12}>
                                    <Typography variant="h6" fontWeight={800} sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <Box sx={{ width: 4, height: 24, bgcolor: Color.primary.main, borderRadius: 2 }} />
                                        Classification
                                    </Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                                        {[
                                            { label: "Category", value: product?.category?.name, icon: <CategoryIcon fontSize="small" /> },
                                            { label: "Sub Category", value: product?.sub_category?.name, icon: <LabelIcon fontSize="small" /> },
                                            { label: "Brand", value: product?.brand?.name, icon: <BrandingWatermarkIcon fontSize="small" /> }
                                        ].map((item, i) => (
                                            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                <Avatar sx={{ bgcolor: alpha(Color.primary.main, 0.05), color: Color.primary.main, width: 40, height: 40 }}>
                                                    {item.icon}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 700, display: "block" }}>{item.label}</Typography>
                                                    <Typography variant="body1" fontWeight={700}>{item.value || "Not Assigned"}</Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Grid>

                                {product?.description && (
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" fontWeight={800} sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Box sx={{ width: 4, height: 24, bgcolor: Color.primary.main, borderRadius: 2 }} />
                                            Description
                                        </Typography>
                                        <Box sx={{
                                            position: "relative",
                                            pl: 3,
                                            borderLeft: `2px solid ${alpha(Color.primary.main, 0.1)}`
                                        }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: "text.secondary",
                                                    lineHeight: 1.7,
                                                    fontWeight: 500,
                                                    fontStyle: "normal"
                                                }}
                                            >
                                                {product?.description}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </>
                ) : null}
            </DialogContent>

            {/* <Box sx={{ px: 5, py: 3, bgcolor: "#f9f9fc", borderTop: "1px solid #f0f0f5" }}>
                <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 800, letterSpacing: 1.5 }}>
                    SVM INVENTORY DOSSIER  •  SYSTEM GENERATED  •  {new Date().toLocaleDateString()}
                </Typography>
            </Box> */}
        </Dialog >
    );
};

export default ViewProduct;
